// import { Table } from 'antd'
// import type { ColumnsType } from 'antd/es/table'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { ServerImageInfo } from '@/api/type-server'
// import InfoI from '@/assets/icons/info-i.svg?react'
// import ResetToDefault from '@/assets/icons/reset.svg?react'
// import { Button } from '@/components/base/Button'
import { Checkbox } from '@/components/base/Checkbox'
import { Dropdown } from '@/components/base/Dropdown'
import { Trans } from '@/components/Trans'
import { MACHINE_ATTR_TAG_OPTIONS, MachineAttrTag } from '@/consts/image'
// import { Image } from '@/models/image'
// import { Pagination } from '@/components/base/Pagination'
import { usePermissionForm } from '@/pages/teams/permissionQuota/PermissionFormContext'
import { fetchAllPages } from '@/utils/pagination'

import { memberPermissionMock } from '../../member/fake.memberPermission'
import { ACCELERATOR_CATALOG } from '../types'
import { useAclRanges } from '../useAclRanges'

interface ImageWithTag {
  id: number
  resID: string
  alias: string
  description: string
  machineAttrTag?: MachineAttrTag
}

type FilterTag = '' | 'all' | MachineAttrTag

// Helper function to map API response to ImageWithTag
const mapImages = (images: ServerImageInfo[], tag: MachineAttrTag): ImageWithTag[] =>
  images.map((img) => ({
    id: img.id,
    resID: img.resID,
    alias: img.alias,
    description: img.description,
    machineAttrTag: tag,
  }))

type Row = {
  id: string
  gpuModel: string
  gpus: number
  gpuRamGB: number
  region: string
  vcpus: number
  ramGB: number
  pricePerDayCredits: number
}

const DISPLAY_TO_PROCESSOR: Record<string, string> = ACCELERATOR_CATALOG.reduce(
  (acc, a) => {
    acc[a.label] = a.processorModel
    return acc
  },
  {} as Record<string, string>,
)

// const isProcessorSelected = (processorModels: string[], model: string) =>
//   processorModels?.includes(model)

export function InstancePermission() {
  const { t } = useTranslation()
  const { form, setForm } = usePermissionForm()
  const { machineCategories, machineGPUModels, machineCPUModels, machineVGPUModels } =
    useAclRanges()

  const [imageList, setImageList] = useState<ImageWithTag[]>([])
  const [selectedTag, setSelectedTag] = useState<FilterTag>('')

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const [containerServerImages, vmServerImages] = await Promise.all([
          fetchAllPages({
            fetchPage: (page, pageSize) =>
              API.getImageList({
                machine_category: 1,
                page,
                per_page: pageSize,
                machine_attr_tag: MachineAttrTag.Container,
              }),
            getItems: (response) => response.images,
          }),
          fetchAllPages({
            fetchPage: (page, pageSize) =>
              API.getImageList({
                machine_category: 1,
                page,
                per_page: pageSize,
                machine_attr_tag: MachineAttrTag.VirtualMachine,
              }),
            getItems: (response) => response.images,
          }),
        ])

        const containerImages = mapImages(containerServerImages, MachineAttrTag.Container)
        const vmImages = mapImages(vmServerImages, MachineAttrTag.VirtualMachine)

        // Merge and dedupe by resID, prefer container tag if duplicate
        const imageMap = new Map<string, ImageWithTag>()
        for (const img of [...containerImages, ...vmImages]) {
          if (!imageMap.has(img.resID)) {
            imageMap.set(img.resID, img)
          }
        }
        setImageList(Array.from(imageMap.values()))
      } catch (error) {
        console.error('Failed to fetch images:', error)
      }
    }
    fetchImages()
  }, [])

  const filteredImageList = useMemo(() => {
    if (!selectedTag || selectedTag === 'all') return imageList
    return imageList.filter((img) => img.machineAttrTag === selectedTag)
  }, [imageList, selectedTag])

  const toModelLabel = (x: any): string => {
    if (x == null) return ''
    if (typeof x === 'string') return x
    return String(x.name ?? x.model ?? x.processorModel ?? x.label ?? '')
  }

  const processorModelsFromAPI = useMemo(() => {
    const mergedRaw = [
      ...(machineGPUModels ?? []),
      ...(machineCPUModels ?? []),
      ...(machineVGPUModels ?? []),
    ]
    const normalized = mergedRaw.map(toModelLabel).filter(Boolean)
    if (normalized.length > 0) return normalized
    return ACCELERATOR_CATALOG.map((a) => a.processorModel)
  }, [machineGPUModels, machineCPUModels, machineVGPUModels])

  const isChecked = (model: string) => (form.processorModels ?? []).includes(model)
  const toggleModel = (model: string) => {
    const set = new Set(form.processorModels ?? [])
    set.has(model) ? set.delete(model) : set.add(model)
    setForm({ ...form, processorModels: Array.from(set.values()) })
  }

  const toggleImage = useCallback(
    (resID: string) => {
      setForm((prev) => {
        const set = new Set(prev.images ?? [])
        set.has(resID) ? set.delete(resID) : set.add(resID)
        return { ...prev, images: Array.from(set) }
      })
    },
    [setForm],
  )

  const gpuSupported = machineCategories?.includes(0)
  const cpuSupported = machineCategories?.includes(1)
  const vgpuSupported = machineCategories?.includes(2)

  const dataSource = useMemo<Row[]>(
    () =>
      memberPermissionMock.instanceTable.rows.map((r: any) => ({
        id: r.id,
        gpuModel: r.gpuModel,
        gpus: r.gpus,
        gpuRamGB: r.gpuRamGB,
        region: r.region,
        vcpus: r.vcpus,
        ramGB: r.ramGB,
        pricePerDayCredits: r.pricePerDayCredits,
      })),
    [],
  )

  //  //select rowKey to gpuModel
  // const rowKey: keyof Row = 'id'

  const [_selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

  useEffect(() => {
    const selectedSet = new Set(form.processorModels ?? [])
    const keys = dataSource
      .filter((r) => selectedSet.has(DISPLAY_TO_PROCESSOR[r.gpuModel] ?? r.gpuModel))
      .map((r) => r.id)
    setSelectedRowKeys(keys)
  }, [form.processorModels, dataSource])

  // const onTableSelectChange = (keys: React.Key[]) => {
  //   setSelectedRowKeys(keys)
  //   const keySet = new Set(keys as string[])
  //   const modelsFromTable = dataSource
  //     .filter((r) => keySet.has(r.id))
  //     .map((r) => DISPLAY_TO_PROCESSOR[r.gpuModel] ?? r.gpuModel)

  //   setForm({ ...form, processorModels: Array.from(new Set(modelsFromTable)) })
  // }

  // const columns: ColumnsType<Row> = [
  //   {
  //     title: <Trans i18nKey="member.gpuModel">GPU Model</Trans>,
  //     dataIndex: 'gpuModel',
  //     key: 'gpuModel',
  //     width: 240,
  //     fixed: 'left',
  //     render: (v: string) => <span className="t-sb text-primary">{v}</span>,
  //   },
  //   { title: <Trans i18nKey="member.gpus">GPUs</Trans>, dataIndex: 'gpus', key: 'gpus', width: 120, align: 'center',
  //     render: (v: number) => <span className="t-sb">{v}</span>,
  //   },
  //   { title: <Trans i18nKey="member.gpuRamGB">GPU RAM</Trans>, dataIndex: 'gpuRamGB', key: 'gpuRamGB', width: 140, align: 'center',
  //     render: (v: number) => <span className="t-sb">{v} GB</span>,
  //   },
  //   { title: <Trans i18nKey="member.region">Region</Trans>, dataIndex: 'region', key: 'region', width: 140, align: 'center',
  //     render: (v: string) => <span className="t-sb">{v}</span>,
  //   },
  //   { title: <Trans i18nKey="member.vcpus">vCPUs</Trans>, dataIndex: 'vcpus', key: 'vcpus', width: 140, align: 'center',
  //     render: (v: number) => (
  //       <span className="relative inline-flex items-center justify-center">
  //         <span className="t-sb tabular-nums mr-2">{v}</span>
  //         <InfoI className="w-4 h-4 opacity-70" aria-hidden="true" />
  //       </span>
  //     ),
  //   },
  //   { title: <Trans i18nKey="member.ramGB">RAM</Trans>, dataIndex: 'ramGB', key: 'ramGB', width: 140, align: 'center',
  //     render: (v: number) => <span className="t-sb">{v} GB</span>,
  //   },
  //   {
  //     title: <Trans i18nKey="member.pricePerDayCredits">Price/day</Trans>,
  //     dataIndex: 'pricePerDayCredits',
  //     key: 'pricePerDayCredits',
  //     width: 180,
  //     fixed: 'right',
  //     align: 'right',
  //     render: (v: number) => (
  //       <div className="text-right">
  //         <div className="t-sb">
  //           {v.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
  //         </div>
  //         <div className="t-sm text-secondary">Credits</div>
  //       </div>
  //     ),
  //   },
  // ]

  // ------- Reset handlers -------
  // const resetInstanceTypes = () => {
  //   if (scope === 'member') {
  //     setForm({
  //       ...form,
  //       gpuMachineCap: teamDefaults?.gpuMachineCap ?? false,
  //       cpuMachineCap: teamDefaults?.cpuMachineCap ?? false,
  //       vgpuMachineCap: teamDefaults?.vgpuMachineCap ?? false,
  //     })
  //   } else {
  //     setForm({ ...form, gpuMachineCap: false, cpuMachineCap: false, vgpuMachineCap: false })
  //   }
  // }

  // const resetAccelerators = () => {
  //   if (scope === 'member') {
  //     setForm({ ...form, processorModels: teamDefaults?.processorModels ?? [] })
  //   } else {
  //     setForm({ ...form, processorModels: [] })
  //   }
  // }

  // const resetAll = () => {
  //   // Reset 整個「實例權限」區塊（機器類型 + 加速器 + 圖片）
  //   if (scope === 'member') {
  //     setForm({
  //       ...form,
  //       gpuMachineCap: teamDefaults?.gpuMachineCap ?? false,
  //       cpuMachineCap: teamDefaults?.cpuMachineCap ?? false,
  //       vgpuMachineCap: teamDefaults?.vgpuMachineCap ?? false,
  //       processorModels: teamDefaults?.processorModels ?? [],
  //       officialImageCap: teamDefaults?.officialImageCap ?? false,
  //       images: teamDefaults?.images ?? [],
  //     })
  //   } else {
  //     setForm({
  //       ...form,
  //       gpuMachineCap: false,
  //       cpuMachineCap: false,
  //       vgpuMachineCap: false,
  //       processorModels: [],
  //       officialImageCap: false,
  //       images: [],
  //     })
  //   }
  // }

  return (
    <div>
      <div className="flex items-center gap-x-2">
        <div className="h-[20px] border-secondary border-solid border-l-4" />
        <span className="h6 text-primary">
          <Trans i18nKey="team.permissionQuota.instancePermission">Instance Permission</Trans>
        </span>
      </div>

      <div className="flex items-center gap-x-2 mt-8">
        <span className="t-sb text-primary">
          <Trans i18nKey="team.permissionQuota.instanceType">Instance Type</Trans>
        </span>
      </div>
      <div className="flex gap-6 py-2">
        {gpuSupported && (
          <Checkbox
            checked={!!form.gpuMachineCap}
            onChange={() => setForm({ ...form, gpuMachineCap: !form.gpuMachineCap })}
            className="px-3 py-2 rounded-[10px] border border-neutral-200 bg-panel-white inline-flex items-center gap-2 cursor-pointer select-none hover:border-primary/40"
            checkedBgColor="bg-success/100"
            checkedBorderColor="border-success/100"
            checkIconColor="after:border-white"
          >
            <span className="t-sb">GPU</span>
          </Checkbox>
        )}
        {cpuSupported && (
          <Checkbox
            checked={!!form.cpuMachineCap}
            onChange={() => setForm({ ...form, cpuMachineCap: !form.cpuMachineCap })}
            className="px-3 py-2 rounded-[10px] border border-neutral-200 bg-panel-white inline-flex items-center gap-2 cursor-pointer select-none hover:border-primary/40"
            checkedBgColor="bg-success/100"
            checkedBorderColor="border-success/100"
            checkIconColor="after:border-white"
          >
            <span className="t-sb">CPU</span>
          </Checkbox>
        )}
        {vgpuSupported && (
          <Checkbox
            checked={!!form.vgpuMachineCap}
            onChange={() => setForm({ ...form, vgpuMachineCap: !form.vgpuMachineCap })}
            className="px-3 py-2 rounded-[10px] border border-neutral-200 bg-panel-white inline-flex items-center gap-2 cursor-pointer select-none hover:border-primary/40"
            checkedBgColor="bg-success/100"
            checkedBorderColor="border-success/100"
            checkIconColor="after:border-white"
          >
            <span className="t-sb">vGPU</span>
          </Checkbox>
        )}
      </div>

      <div className="flex items-center gap-x-2 mt-8">
        <span className="t-sb text-primary">
          <Trans i18nKey="team.permissionQuota.acceleratorPermission">Accelerator Permission</Trans>
        </span>
      </div>
      <div className="flex flex-wrap gap-3 py-2">
        {processorModelsFromAPI.map((model) => (
          <Checkbox
            key={String(model)}
            checked={isChecked(String(model))}
            onChange={() => toggleModel(String(model))}
            className={[
              'px-3 py-3 rounded-[10px] border border-neutral-200 bg-panel-white',
              'inline-flex items-center gap-2 cursor-pointer select-none',
              'hover:border-primary/40 whitespace-nowrap',
            ].join(' ')}
            checkedBgColor="bg-success/100"
            checkedBorderColor="border-success/100"
            checkIconColor="after:border-white"
          >
            <span className="t-sb">{String(model)}</span>
          </Checkbox>
        ))}
      </div>

      {/* select rowKey to gpuModel and sync to form.processorModels */}
      {/* todo when backend ready */}
      {/* <div className="mt-4">
        <span className="t-sb text-primary">
          <Trans i18nKey="member.instancePermission">Instance Permission</Trans>
        </span>

        <Table<Row>
          className="mt-4"
          rowKey={rowKey as any}
          dataSource={dataSource}
          columns={columns}
          sticky
          pagination={false}
          rowSelection={{
            selectedRowKeys,
            onChange: onTableSelectChange,
          }}
        />

        <Pagination
          className="justify-end mt-4"
          size={memberPermissionMock.instanceTable.pagination.pageSize}
          total={memberPermissionMock.instanceTable.pagination.total}
          current={memberPermissionMock.instanceTable.pagination.page}
          onChange={() => {}}
        />
      </div> */}

      {/* Image Type + Official Images → 綁定 official_image_cap / images */}
      <div className="mt-8">
        <span className="t-sb text-primary">
          <Trans i18nKey="team.permissionQuota.imageType">Image Type</Trans>
        </span>
        <div className="mt-8">
          <Checkbox
            checked={!!form.officialImageCap}
            onChange={() => setForm({ ...form, officialImageCap: !form.officialImageCap })}
            className={[
              'px-4 py-3 rounded-[10px] border border-neutral-200 bg-panel-white',
              'inline-flex items-center gap-2 cursor-pointer select-none',
              'hover:border-primary/40',
            ].join(' ')}
            checkedBgColor="bg-success/100"
            checkedBorderColor="border-success/100"
            checkIconColor="after:border-white"
          >
            <span className="t-sb">
              <Trans i18nKey="team.permissionQuota.officialImage">Official Image</Trans>
            </span>
          </Checkbox>
        </div>
      </div>

      <div className="mt-8">
        <span className="t-sb text-primary">
          <Trans i18nKey="team.permissionQuota.officialImagePermission">
            Official Image Permission
          </Trans>
        </span>

        {/* Type filter dropdown */}
        <div className="flex items-center gap-2 mt-4 mb-4">
          <span className="text-tertiary t-xs">{t('customImage.filter.title', 'Type')}</span>
          <Dropdown
            options={MACHINE_ATTR_TAG_OPTIONS.map((opt) => ({
              value: opt.value,
              label: t(opt.labelKey),
            }))}
            value={selectedTag}
            showAllOption
            allOptionLabel={t('common.any', 'Any')}
            onChange={(v) => setSelectedTag(v as FilterTag)}
            rootClassName="w-44"
            className="t-x-sb h-8"
          />
        </div>

        <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4 overflow-y-auto max-h-[450px]">
          {filteredImageList.map((image: ImageWithTag) => {
            const checked = (form.images ?? []).includes(image.resID)
            return (
              <Checkbox
                key={image.id}
                checked={checked}
                onChange={() => toggleImage(image.resID)}
                className={[
                  'p-4 rounded-lg border border-neutral-200 bg-panel-white',
                  'inline-flex items-center gap-2 cursor-pointer select-none',
                  'hover:border-primary/40 w-full h-[140px]',
                ].join(' ')}
                checkedBgColor="bg-success/100"
                checkedBorderColor="border-success/100"
                checkIconColor="after:border-white"
              >
                <div className="flex flex-col gap-y-1">
                  <div className="t-xs">{image.resID}</div>
                  <div className="t-sb">{image.alias}</div>
                  <div className="t-xs text-secondary">{image.description}</div>
                </div>
              </Checkbox>
            )
          })}
        </div>
      </div>

      {/* Billing Type 目前 API 沒欄位 先保留 UI，不寫回 form / API */}
      {/* <div className="mt-6">
        <span className="t-sb text-primary">
          <Trans i18nKey="member.billingType">Billing Type</Trans>
        </span>
        <div className="flex gap-4 mt-4">
          <Checkbox
            checked={memberPermissionMock.billing.subscription}
            onChange={() => {}}
            className="px-4 py-4 rounded-[10px] border border-neutral-200 bg-panel-white inline-flex items-center gap-2 cursor-pointer select-none hover:border-primary/40"
            checkedBgColor="bg-success/100"
            checkedBorderColor="border-success/100"
            checkIconColor="after:border-white"
          >
            <span className="t-sb">Subscription</span>
          </Checkbox>
          <Checkbox
            checked={memberPermissionMock.billing.payg}
            onChange={() => {}}
            className="px-4 py-4 rounded-[10px] border border-neutral-200 bg-panel-white inline-flex items-center gap-2 cursor-pointer select-none hover:border-primary/40"
            checkedBgColor="bg-success/100"
            checkedBorderColor="border-success/100"
            checkIconColor="after:border-white"
          >
            <span className="t-sb">Pay-as-you-go</span>
          </Checkbox>
        </div>
      </div> */}
    </div>
  )
}
